// Defaults and normalisation for configuration objects. Used by the server to
// sanitise admin/API/import input and by the admin UI when creating new items.

import type {
  OutputConfig,
  AudioIoConfig,
  EncodeSettings,
  PositionConfig,
  SourceConfig,
  TallyProviderConfig,
  TallyProviderKind,
  TruckSink,
} from './types';

const num = (v: unknown, def: number, min: number, max: number): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : def;
};
const str = (v: unknown, def: string): string => (typeof v === 'string' ? v : def);
const bool = (v: unknown, def: boolean): boolean => (typeof v === 'boolean' ? v : def);
function oneOf<T extends string>(v: unknown, allowed: readonly T[], def: T): T {
  return allowed.includes(v as T) ? (v as T) : def;
}
const pair = (v: unknown, def: [number, number]): [number, number] =>
  Array.isArray(v) && v.length === 2 ? [num(v[0], def[0], 1, 64), num(v[1], def[1], 1, 64)] : def;

export function newId(prefix: string): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}${rand}`;
}

export function normalizeEncode(e: Partial<EncodeSettings> | undefined): EncodeSettings {
  const x = e ?? {};
  const low = (x.lowLayer ?? {}) as Partial<EncodeSettings['lowLayer']>;
  return {
    width: num(x.width, 1280, 160, 3840),
    height: num(x.height, 720, 90, 2160),
    framerate: num(x.framerate, 0, 0, 120),
    bitrateKbps: num(x.bitrateKbps, 3500, 200, 50000),
    gopMs: num(x.gopMs, 1000, 100, 1000),
    encoder: oneOf(x.encoder, ['auto', 'x264', 'nvh264', 'vaapi', 'qsv'] as const, 'auto'),
    rateControl: oneOf(x.rateControl, ['cbr', 'vbr'] as const, 'cbr'),
    profile: oneOf(x.profile, ['constrained-baseline', 'main'] as const, 'constrained-baseline'),
    deinterlace: oneOf(x.deinterlace, ['auto', 'on', 'off'] as const, 'auto'),
    burnIn: bool(x.burnIn, false),
    lowLayer: {
      enabled: bool(low.enabled, false),
      width: num(low.width, 640, 160, 1920),
      height: num(low.height, 360, 90, 1080),
      bitrateKbps: num(low.bitrateKbps, 700, 100, 5000),
    },
  };
}

export function normalizeSource(s: Partial<SourceConfig>): SourceConfig {
  const kind = oneOf(s.kind, ['test', 'decklink', 'ndi'] as const, 'test');
  const audio = (s.audio ?? {}) as Partial<SourceConfig['audio']>;
  const out: SourceConfig = {
    id: str(s.id, '') || newId('src'),
    name: str(s.name, 'Source').slice(0, 64),
    kind,
    enabled: bool(s.enabled, true),
    encode: normalizeEncode({ burnIn: kind === 'test', ...s.encode }),
    audio: {
      enabled: bool(audio.enabled, true),
      channelPair: pair(audio.channelPair, [1, 2]),
      inputChannels: num(audio.inputChannels, kind === 'decklink' ? 8 : 2, 1, 64),
      bitrateKbps: num(audio.bitrateKbps, 96, 16, 256),
    },
  };
  if (kind === 'test') {
    out.test = { pattern: str(s.test?.pattern, 'smpte').replace(/[^a-z0-9-]/gi, ''), tone: bool(s.test?.tone, true) };
  } else if (kind === 'decklink') {
    out.decklink = {
      device: num(s.decklink?.device, 0, 0, 63),
      connection: oneOf(s.decklink?.connection, ['auto', 'sdi', 'hdmi'] as const, 'sdi'),
      mode: str(s.decklink?.mode, 'auto').replace(/[^a-z0-9.]/gi, '') || 'auto',
    };
  } else {
    out.ndi = { name: str(s.ndi?.name, ''), bandwidth: oneOf(s.ndi?.bandwidth, ['highest', 'lowest'] as const, 'highest') };
  }
  return out;
}

export function normalizePosition(p: Partial<PositionConfig>): PositionConfig {
  return {
    id: str(p.id, '') || newId('pos'),
    number: num(p.number, 1, 0, 999),
    name: str(p.name, `CAM ${p.number ?? 1}`).slice(0, 64),
    sourceId: typeof p.sourceId === 'string' && p.sourceId ? p.sourceId : null,
    tally: Array.isArray(p.tally)
      ? p.tally
          .filter((m) => m && typeof m.providerId === 'string')
          .map((m) => ({ providerId: m.providerId, input: String(m.input ?? '').trim() }))
          .filter((m) => m.input !== '')
      : [],
    group: typeof p.group === 'string' && p.group ? p.group : null,
    exclusive: bool(p.exclusive, false),
    allowVideo: bool(p.allowVideo, false),
  };
}

const DEFAULT_PORTS: Partial<Record<TallyProviderKind, number>> = { atem: 9910, vmix: 8099, tsl31: 4001, tsl5: 4003 };

export function normalizeTallyProvider(t: Partial<TallyProviderConfig>): TallyProviderConfig {
  const kind = oneOf(t.kind, ['atem', 'vmix', 'tsl31', 'tsl5', 'mock'] as const, 'mock');
  const out: TallyProviderConfig = {
    id: str(t.id, '') || newId('tally'),
    name: str(t.name, kind.toUpperCase()).slice(0, 64),
    kind,
    enabled: bool(t.enabled, true),
  };
  if (kind === 'atem' || kind === 'vmix') {
    out.host = str(t.host, '');
    out.port = num(t.port, DEFAULT_PORTS[kind]!, 1, 65535);
  }
  if (kind === 'atem') out.me = num(t.me, 0, 0, 3);
  if (kind === 'tsl31' || kind === 'tsl5') {
    out.port = num(t.port, DEFAULT_PORTS[kind]!, 1, 65535);
    out.transport = oneOf(t.transport, ['udp', 'tcp'] as const, 'udp');
    out.tslProgram = num(t.tslProgram, 1, 1, 4);
    out.tslPreview = num(t.tslPreview, 2, 1, 4);
    out.staleAfterSec = num(t.staleAfterSec, 0, 0, 3600);
  }
  if (kind === 'mock') {
    out.mock = { inputs: num(t.mock?.inputs, 8, 1, 64), autoCycleSec: num(t.mock?.autoCycleSec, 0, 0, 3600) };
  }
  return out;
}

function normalizeSink(s: Partial<TruckSink>): TruckSink {
  const kind = oneOf(s.kind, ['alsa', 'pulse', 'decklink', 'ndi'] as const, 'alsa');
  const out: TruckSink = { kind };
  if (kind === 'alsa' || kind === 'pulse') out.device = str(s.device, kind === 'alsa' ? 'default' : '');
  if (kind === 'decklink') {
    out.decklinkDevice = num(s.decklinkDevice, 0, 0, 63);
    out.decklinkMode = str(s.decklinkMode, '1080i50').replace(/[^a-z0-9.]/gi, '');
  }
  if (kind === 'alsa' || kind === 'decklink') {
    out.outputChannels = num(s.outputChannels, kind === 'decklink' ? 8 : 2, 2, 64);
    out.channelPair = pair(s.channelPair, [1, 2]);
  }
  if (kind === 'ndi') out.ndiName = str(s.ndiName, 'Talkback Out');
  return out;
}

export function normalizeAudio(a: Partial<AudioIoConfig> | undefined): AudioIoConfig {
  const x = a ?? {};
  const ti = (x.truckInput ?? {}) as Partial<AudioIoConfig['truckInput']>;
  const to = (x.truckOutput ?? {}) as Partial<AudioIoConfig['truckOutput']>;
  return {
    truckInput: {
      kind: oneOf(ti.kind, ['none', 'test', 'alsa', 'pulse', 'decklink', 'ndi', 'source-pair'] as const, 'none'),
      label: str(ti.label, 'Truck').slice(0, 32),
      device: typeof ti.device === 'string' ? ti.device : undefined,
      decklinkDevice: ti.decklinkDevice === undefined ? undefined : num(ti.decklinkDevice, 0, 0, 63),
      inputChannels: num(ti.inputChannels, 2, 1, 64),
      channelPair: pair(ti.channelPair, [1, 2]),
      ndiName: typeof ti.ndiName === 'string' ? ti.ndiName : undefined,
      sourceId: typeof ti.sourceId === 'string' ? ti.sourceId : undefined,
      gainDb: num(ti.gainDb, 0, -40, 20),
    },
    truckOutput: {
      enabled: bool(to.enabled, false),
      sinks: Array.isArray(to.sinks) ? to.sinks.map(normalizeSink) : [],
      gainDb: num(to.gainDb, 0, -40, 20),
      includeDirectors: bool(to.includeDirectors, false),
      slots: num(to.slots, 8, 1, 64),
    },
    partyLineScope: oneOf(x.partyLineScope, ['all', 'group'] as const, 'all'),
    groups: Array.isArray(x.groups) ? [...new Set(x.groups.map(String).filter(Boolean))].slice(0, 32) : [],
    duckDb: num(x.duckDb, -12, -60, 0),
  };
}

/** DeckLink output modes we can generate caps for. */
export const DECKLINK_OUTPUT_MODES: Record<string, { width: number; height: number; fps: [number, number]; interlaced: boolean }> = {
  '1080i50': { width: 1920, height: 1080, fps: [25, 1], interlaced: true },
  '1080i5994': { width: 1920, height: 1080, fps: [30000, 1001], interlaced: true },
  '1080p25': { width: 1920, height: 1080, fps: [25, 1], interlaced: false },
  '1080p2997': { width: 1920, height: 1080, fps: [30000, 1001], interlaced: false },
  '1080p30': { width: 1920, height: 1080, fps: [30, 1], interlaced: false },
  '1080p50': { width: 1920, height: 1080, fps: [50, 1], interlaced: false },
  '1080p5994': { width: 1920, height: 1080, fps: [60000, 1001], interlaced: false },
  '1080p60': { width: 1920, height: 1080, fps: [60, 1], interlaced: false },
  '720p50': { width: 1280, height: 720, fps: [50, 1], interlaced: false },
  '720p5994': { width: 1280, height: 720, fps: [60000, 1001], interlaced: false },
  '720p60': { width: 1280, height: 720, fps: [60, 1], interlaced: false },
};

export function normalizeOutput(o: Partial<OutputConfig>): OutputConfig {
  const kind = oneOf(o.kind, ['ndi', 'decklink'] as const, 'ndi');
  const out: OutputConfig = {
    id: str(o.id, '') || newId('out'),
    name: str(o.name, 'Output').slice(0, 64),
    enabled: bool(o.enabled, true),
    sourceId: typeof o.sourceId === 'string' && o.sourceId ? o.sourceId : null,
    kind,
    width: num(o.width, 1280, 160, 3840),
    height: num(o.height, 720, 90, 2160),
    framerate: num(o.framerate, 25, 1, 60),
  };
  if (kind === 'ndi') out.ndiName = str(o.ndiName, out.name).slice(0, 64);
  else {
    out.decklinkDevice = num(o.decklinkDevice, 0, 0, 63);
    out.decklinkMode = oneOf(o.decklinkMode, Object.keys(DECKLINK_OUTPUT_MODES), '1080i50');
  }
  return out;
}
